import React, { Component } from "react";
import { action } from "mobx";
import { observer } from "mobx-react";

import { duration } from "@formant/core/model/duration";
import { IAnnotation } from "@formant/core/model/IAnnotation";
import { IComment } from "@formant/core/model/IComment";
import { IDevice } from "@formant/core/model/IDevice";
import { IEventOrAggregate } from "@formant/core/model/IEventOrAggregate";
import { IFilter } from "@formant/core/model/IFilter";
import { IObserveLayout } from "@formant/core/model/IObserveLayout";
import { IPartialViewConfiguration } from "@formant/core/model/IPartialViewConfiguration";
import { IShare } from "@formant/core/model/IShare";
import { IViewConfiguration } from "@formant/core/model/IViewConfiguration";
import { ModuleMode } from "@formant/core/model/ModuleMode";
import { ModuleType } from "@formant/core/model/ModuleType";
import { StreamType } from "@formant/core/model/StreamType";
import { Page } from "@formant/ui/component/common/Page";

import { SeekOption } from "../../store/SeekOption";
import { appStore, deviceStore, eventStore, observeStore } from "../../stores";
import { localSettingObservable } from "../../utils/localSettingObservable";
import { RestrictedComponent } from "../common/RestrictedComponent";
import { Scrollable } from "../common/Scrollable";
import { Timer } from "../common/Timer";
import { DeviceConfigurationPopup } from "./DeviceConfigurationPopup";
import { EventDetails } from "./EventDetails";
import { EventSidebar } from "./EventSidebar";
import { ExportTelemetryPopup } from "./ExportTelemetryPopup";
import { HelpHints } from "./HelpHints";
import { Modules } from "./Modules";
import * as styles from "./ObserveLayout.scss";
import { Timeline } from "./Timeline";
import { ValueTooltip } from "./ValueTooltip";

export interface IObserveLayoutProps {
    context: IFilter;
    device?: IDevice;
    filter?: IFilter;
    layout: IObserveLayout;
    onSaveLayout?: (layout: IObserveLayout) => Promise<void>;
    configuration: IViewConfiguration[];
    share?: IShare;
    noGrid?: boolean;
    onChangeFilter?: (filter: IFilter) => void;
    onChangeConfiguration?: (value: IPartialViewConfiguration) => void;
    onChangeLayout?: (layout: IObserveLayout) => void;
    onRemoveModule?: (streamName: string) => void;
    commentEnabled?: boolean;
    annotateEnabled?: boolean;
    demandEnabled?: boolean;
    hideDevice: boolean;
}

@observer
export class ObserveLayout extends Component<IObserveLayoutProps> {
    @localSettingObservable("observe.show-focus-message")
    private showFocusMessage: boolean = true;

    private onTick = () => {
        observeStore.setTime(new Date());
    };

    public render() {
        const {
            onSelectEvent,
            onSelectTime,
            onSeek,
            onChangeAnnotation,
            onChangeSeekOption,
            onComment,
            onAnnotate,
            onDemandData,
        } = this;
        const {
            context,
            device,
            filter,
            layout,
            configuration,
            share,
            noGrid,
            onChangeFilter,
            onChangeConfiguration,
            commentEnabled,
            annotateEnabled,
            demandEnabled,
            hideDevice,
            onChangeLayout,
            onRemoveModule,
        } = this.props;

        const {
            focusedStream,
            selectedEventId,
            fullscreen,
            editingAnnotation,
            seekOption,
        } = observeStore;
        const selectedEvent =
            (selectedEventId && eventStore.get(selectedEventId)) || undefined;
        const activeEvent = editingAnnotation || selectedEvent;

        return (
            <Page className={styles.observe}>
                <div className={styles["content-container"]}>
                    <Scrollable
                        className={styles.content}
                        dark={true}
                        preventScroll={fullscreen}
                    >
                        <HelpHints context={context} />
                        <EventDetails
                            event={selectedEvent}
                            onClose={this.onCloseEvent}
                        />
                        <Modules
                            context={context}
                            filter={filter}
                            layout={layout}
                            configuration={configuration}
                            device={device}
                            activeEvent={activeEvent}
                            focusedModule={focusedStream}
                            fullscreen={fullscreen}
                            onSelectTime={onSelectTime}
                            onSeek={onSeek}
                            onChangeFilter={onChangeFilter}
                            onChangeConfiguration={onChangeConfiguration}
                            onChangeLayout={onChangeLayout}
                            onFocusModule={
                                observeStore.configuringModule
                                    ? undefined
                                    : this.onFocusModule
                            }
                            onToggleFullscreen={this.onToggleFullscreen}
                            noGrid={noGrid}
                            onComment={commentEnabled ? onComment : undefined}
                            onAnnotate={
                                annotateEnabled ? onAnnotate : undefined
                            }
                            onDemandData={
                                demandEnabled ? onDemandData : undefined
                            }
                            onRemoveModule={
                                onRemoveModule ? this.onRemoveModule : undefined
                            }
                            hideDevice={hideDevice}
                            share={share}
                            onConfigureModule={this.onConfigureModule}
                            moduleMode={ModuleMode.Observe}
                        />
                    </Scrollable>
                    {!share && (
                        <EventSidebar
                            context={context}
                            filter={filter}
                            onAnnotate={onAnnotate}
                            onSeek={onSeek}
                            onSelectEvent={onSelectEvent}
                        />
                    )}
                </div>
                <ValueTooltip />
                {!appStore.preview && (
                    <Timeline
                        context={context}
                        filter={filter}
                        focusedStream={focusedStream}
                        share={share}
                        onSeek={onSeek}
                        editingEvent={editingAnnotation}
                        selectedEvent={selectedEvent}
                        onChangeEditingEvent={onChangeAnnotation}
                        onSelectEvent={onSelectEvent}
                        device={device}
                        seekOption={seekOption}
                        onChangeSeekOption={onChangeSeekOption}
                    />
                )}
                {appStore.preview && (
                    <Timer
                        interval={0.7 * duration.second}
                        onTick={this.onTick}
                    />
                )}
                <RestrictedComponent
                    resource="integrations"
                    requiredAccess="operator"
                >
                    {!share && observeStore.exportTelemetryPopupQuery && (
                        <ExportTelemetryPopup
                            query={observeStore.exportTelemetryPopupQuery}
                        />
                    )}
                </RestrictedComponent>
                {device && (
                    <DeviceConfigurationPopup
                        device={device}
                        onClose={this.onEditDeviceClose}
                    />
                )}
            </Page>
        );
    }

    private onEditDeviceClose = () => {
        observeStore.editingDevice = false;
    };

    private onFocusModule = (stream?: [string, ModuleType?]) => {
        const isCustom = stream;
        const { layout } = this.props;
        const { showFocusMessage } = this;
        const [streamName] = stream || [];
        const moduleConfig = streamName
            ? layout[streamName]?.configuration
            : undefined;
        const name =
            moduleConfig &&
            (isCustom || moduleConfig.customVisualizationUrl) &&
            moduleConfig.customName
                ? moduleConfig.customName
                : streamName;
        if (showFocusMessage && streamName !== undefined) {
            appStore.setSnackbar(
                <>
                    Timeline focused on{" "}
                    <span key="streamname" className={styles.streamName}>
                        {name}
                    </span>{" "}
                    module
                </>,
                {
                    label: "Don't show again",
                    onClick: () => {
                        this.showFocusMessage = false;
                    },
                },
                "top",
            );
        }
        observeStore.focusedStream = stream
            ? [stream[0], stream[1]]
            : undefined;
    };

    @action
    private onToggleFullscreen = (stream?: [string, ModuleType?]) => {
        if (stream) {
            observeStore.focusedStream = stream as
                | [string, StreamType?]
                | undefined;
        }
        observeStore.toggleFullscreen();
    };

    private onSeek = async (time: Date) => {
        await this.onSelectTime(time);
    };

    private onSelectTime = async (time: Date) => {
        if (time > new Date()) {
            // never allow a jump into the future
            return;
        }
        observeStore.pauseAt(time);
    };

    private onCloseEvent = () => {
        observeStore.showEventList();
    };

    private onSelectEvent = (eventOrAggregate: IEventOrAggregate) => {
        const { event, aggregate } = eventOrAggregate;
        if (event) {
            observeStore.viewEvent(event);
        } else if (aggregate) {
            observeStore.showEventList(aggregate);
        }
    };

    private onChangeAnnotation = (annotation: IAnnotation) => {
        observeStore.editingAnnotation = annotation;
    };

    private onChangeSeekOption = (seekOption: SeekOption) => {
        observeStore.seekOption = seekOption;
    };

    private onComment = (comment: Partial<IComment>): void => {
        observeStore.editComment(comment);
    };

    private onAnnotate = (annotation: Partial<IAnnotation>): void => {
        observeStore.editAnnotation(annotation);
    };

    private onDemandData = (streamName: string) => {
        appStore.onDemandData(streamName);
    };

    private onRemoveModule = (streamName: string) => {
        const { onRemoveModule } = this.props;
        if (onRemoveModule) {
            appStore.setConfirm({
                title: "Delete module",
                okCopy: "Delete module",
                message: (
                    <>
                        Are you sure you want to delete this module?
                        <br />
                        Might take a minute while we refresh the dashboard.
                    </>
                ),
                critical: true,
                onOk: () => {
                    onRemoveModule(streamName);
                    observeStore.fullscreen = false;
                },
            });
        }
    };

    public componentDidMount() {
        if (this.props.device?.id) {
            deviceStore.setFocusedDevice(this.props.device.id);
        }
    }

    public componentWillUnmount() {
        deviceStore.unsetFocusedDevice();
    }

    onConfigureModule = async (moduleName: string) => {
        observeStore.temporarilyConfiguringModule = [moduleName];
    };
}
